import { useMutation } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import { notification } from "@neonumy/neonumy-ui";
import { FormInstance } from "antd5";
import useStore from "config/store";
import { CREATE_UPDATE_USER_QUERY } from "queries/neoauth/users";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getFormErrors } from "utils/getFormErrors";
import { onErrorGraphql } from "utils/onErrorGraphql";

interface CreateUpdateUserMutationProps {
  form: FormInstance;
  isEdit: boolean;
  onMutationCompleted: (userId?: ID) => void;
}

export default function useCreateUpdateUserMutation({
  form,
  isEdit,
  onMutationCompleted,
}: CreateUpdateUserMutationProps) {
  const { t } = useTranslation();

  const setCreateUpdateUserLoading = useStore(state => state.setCreateUpdateUserLoading);

  const [createUpdateUser, { loading }] = useMutation<
    CreateUpdateUserOutput,
    CreateUpdateUserMutationInput
  >(CREATE_UPDATE_USER_QUERY, {
    client: clientNeoAuth,
    onCompleted: ({ createUpdateUser: data }) => {
      if (data?.errors?.length > 0) {
        const fieldValues = form.getFieldsValue(true);
        const fieldsData = getFormErrors(data.errors, fieldValues);
        if (fieldsData) {
          form.setFields(fieldsData);
        }

        const allError = data.errors.find(error => error.field === "__all__");
        if (allError) {
          notification.error({
            message: allError.messages.join(". "),
            placement: "bottomRight",
          });
        }

        if (!isEdit) {
          onMutationCompleted();
        }
      } else {
        handleSuccess(data.user.id);
      }
    },
    onError: error => {
      onErrorGraphql(error);
      onMutationCompleted();
    },
  });

  const handleSuccess = (userId: ID) => {
    if (isEdit) {
      notification.success({
        message: t("Saved"),
        description: t("Collaborator was saved with success"),
        placement: "bottomLeft",
      });
      onMutationCompleted(userId);
    } else {
      onMutationCompleted(userId);
    }
  };

  useEffect(() => {
    setCreateUpdateUserLoading(loading);
  }, [loading]);

  return { createUpdateUser, loading };
}
